// ゲームのセーブ。ログイン中は Firestore（users/{uid}/saves/{gameId}）、ゲストは端末の中（localStorage）。
// 1本のゲームのセーブは1つの文書にまとめ、JSON の文字列で持つ（ルールで 64KB までに制限できるため）。
import { LIMITS, type Json } from "@appful/sdk/protocol";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { session } from "./auth";
import { db } from "./firebase";

type Data = Record<string, Json>;

interface Slot {
  owner: string; // uid か "guest"
  gameId: string;
  data: Data;
  rev: number;
  writing: Promise<void> | null;
  dirty: boolean;
}

const slots = new Map<string, Slot>();
const guestKey = (gameId: string) => `appful:save:${gameId}`;

export class SaveTooLargeError extends Error {
  code = "too_large" as const;
}

function readGuest(gameId: string): Data | null {
  try {
    const raw = localStorage.getItem(guestKey(gameId));
    return raw ? (JSON.parse(raw) as Data) : null;
  } catch {
    return null;
  }
}

function parse(text: unknown): Data {
  if (typeof text !== "string") return {};
  try {
    const v = JSON.parse(text) as unknown;
    return v && typeof v === "object" && !Array.isArray(v) ? (v as Data) : {};
  } catch {
    return {};
  }
}

async function load(gameId: string): Promise<Slot> {
  const uid = session().user?.uid ?? null;
  const owner = uid ?? "guest";
  const key = `${owner}:${gameId}`;
  const cached = slots.get(key);
  if (cached) return cached;

  let slot: Slot;
  if (!uid) {
    slot = { owner, gameId, data: readGuest(gameId) ?? {}, rev: 0, writing: null, dirty: false };
  } else {
    const snap = await getDoc(doc(db, "users", uid, "saves", gameId));
    if (snap.exists()) {
      slot = { owner, gameId, data: parse(snap.get("data")), rev: snap.get("rev") as number, writing: null, dirty: false };
    } else {
      // はじめてログインして遊ぶゲーム：ゲストのときのセーブがあれば引き継ぐ。
      const guest = readGuest(gameId);
      slot = { owner, gameId, data: guest ?? {}, rev: 0, writing: null, dirty: false };
      if (guest) void flush(slot);
    }
  }
  slots.set(key, slot);
  return slot;
}

async function writeCloud(slot: Slot): Promise<void> {
  const ref = doc(db, "users", slot.owner, "saves", slot.gameId);
  const body = () => ({ data: JSON.stringify(slot.data), rev: slot.rev + 1, updatedAt: serverTimestamp() });
  try {
    await setDoc(ref, body());
    slot.rev += 1;
  } catch (e) {
    // rev が合わない＝ほかの端末が先に書いた。最新の rev を読み、手元の中身で上書きする（後の書き込みが勝つ）。
    if ((e as { code?: string }).code !== "permission-denied") throw e;
    const snap = await getDoc(ref);
    slot.rev = snap.exists() ? (snap.get("rev") as number) : 0;
    await setDoc(ref, body());
    slot.rev += 1;
  }
}

/** 書いている途中に次の変更が来たら、終わってからもう1回だけ書く（連続の書き込みをまとめる）。 */
function flush(slot: Slot): Promise<void> {
  if (slot.owner === "guest") {
    localStorage.setItem(guestKey(slot.gameId), JSON.stringify(slot.data));
    return Promise.resolve();
  }
  if (slot.writing) {
    slot.dirty = true;
    return slot.writing;
  }
  slot.writing = (async () => {
    try {
      do {
        slot.dirty = false;
        await writeCloud(slot);
      } while (slot.dirty);
    } finally {
      slot.writing = null;
    }
  })();
  return slot.writing;
}

/** 書き込みの完了を待つのは最大この時間。過ぎても書き込みは裏で続く。 */
const WAIT_MS = 4_000;

async function commit(slot: Slot): Promise<{ cloud: boolean }> {
  const write = flush(slot);
  if (slot.owner === "guest") return { cloud: false };
  const ok = await Promise.race([
    write.then(() => true),
    new Promise<boolean>((r) => setTimeout(() => r(false), WAIT_MS)),
  ]).catch((e) => {
    console.error("[saves] cloud write failed", e);
    return false;
  });
  return { cloud: ok };
}

export async function getValue(gameId: string, key: string): Promise<Json | null> {
  const slot = await load(gameId);
  return slot.data[key] ?? null;
}

/** maxChars：短いゲームは 64K 文字、長いゲームは 256K 文字（LIMITS） */
export async function setValue(gameId: string, key: string, value: Json, maxChars: number = LIMITS.saveBytes): Promise<{ cloud: boolean }> {
  const slot = await load(gameId);
  const next = { ...slot.data, [key]: value };
  if (JSON.stringify(next).length > maxChars) throw new SaveTooLargeError(`save exceeds ${maxChars / 1024}K characters`);
  slot.data = next;
  return commit(slot);
}

export async function removeValue(gameId: string, key: string): Promise<{ cloud: boolean }> {
  const slot = await load(gameId);
  if (!(key in slot.data)) return { cloud: slot.owner !== "guest" };
  const next = { ...slot.data };
  delete next[key];
  slot.data = next;
  return commit(slot);
}

/** ログインの状態が変わったら、読み込んだセーブを捨てる（次に読むときに読み直す）。 */
export function resetSaves() {
  slots.clear();
}
